import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { API_URL } from '../config';
import Header from "../components/header";
import DefaultFooter from "../components/default-footer";
import WhatsAppButton from "../components/WhatsAppButton";

function ForgotPassword(): React.JSX.Element {
    const navigate = useNavigate();
    const [step, setStep] = useState<number>(1); // 1: Email, 2: OTP, 3: New Password
    const [email, setEmail] = useState<string>('');
    const [otpCode, setOtpCode] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
    
    // Toggle password visibility states
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showPasswordConfirmation, setShowPasswordConfirmation] = useState<boolean>(false);

    const [loading, setLoading] = useState<boolean>(false);

    // Step 1: Send OTP
    const handleSendOtp = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);

        try {
            const res = await fetch(`${API_URL}/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Error sending email.');

            toast.success('Verification code has been sent to your email.');
            setStep(2);
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Verify OTP
    const handleVerifyOtp = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);

        try {
            const res = await fetch(`${API_URL}/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ email, otp_code: otpCode })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Invalid OTP code.');

            toast.success('Code verified successfully. Enter your new password.');
            setStep(3);
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Step 3: Reset Password
    const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (password !== passwordConfirmation) {
            toast.error('Passwords do not match.');
            return;
        }

        setLoading(true);

        try {
            const res = await fetch(`${API_URL}/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ 
                    email, 
                    otp_code: otpCode, 
                    password, 
                    password_confirmation: passwordConfirmation 
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Error resetting password.');

            toast.success('Password updated successfully! Please log in.');
            setTimeout(() => {
                navigate('/login');
            }, 1500);
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page">
            <Toaster position="top-center" />
            <Header />
            <section className="layout-pt-lg layout-pb-lg">
                <div className="container">
                    <div className="row justify-center">
                        <div className="col-xl-5 col-lg-6 col-md-8">
                            <div className="login-card" style={{ background: '#fff', padding: '40px', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(41, 75, 90, 0.25)' }}>
                                
                                <h2 className="corp-section-title" style={{ marginBottom: '20px', textAlign: 'center' }}>
                                    {step === 1 && <span>Forgot <em>Password</em></span>}
                                    {step === 2 && <span>OTP <em>Verification</em></span>}
                                    {step === 3 && <span>New <em>Password</em></span>}
                                </h2>

                                {/* STEP 1: Email Form */}
                                {step === 1 && (
                                    <form onSubmit={handleSendOtp}>
                                        <div className="corp-input-group" style={{ marginBottom: '20px' }}>
                                            <label className="corp-input-label">Your email address</label>
                                            <input 
                                                type="email" 
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="corp-input"
                                                placeholder="your@email.com"
                                                style={{ width: '100%', padding: '12px', border: '2px solid #cbd5e1', borderRadius: '8px' }}
                                                required 
                                            />
                                        </div>
                                        <button type="submit" className="corp-btn-primary" disabled={loading} style={{ width: '100%', padding: '14px', background: '#294b5a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                                            {loading ? 'Sending...' : 'Send OTP Code'}
                                        </button>
                                    </form>
                                )}

                                {/* STEP 2: OTP Code Form */}
                                {step === 2 && (
                                    <form onSubmit={handleVerifyOtp}>
                                        <div className="corp-input-group" style={{ marginBottom: '20px' }}>
                                            <label className="corp-input-label">Enter the 6-digit code received by email</label>
                                            <input 
                                                type="text" 
                                                maxLength={6}
                                                value={otpCode}
                                                onChange={(e) => setOtpCode(e.target.value)}
                                                className="corp-input"
                                                placeholder="123456"
                                                style={{ width: '100%', padding: '12px', border: '2px solid #cbd5e1', borderRadius: '8px', textAlign: 'center', letterSpacing: '4px', fontSize: '18px' }}
                                                required 
                                            />
                                        </div>
                                        <button type="submit" className="corp-btn-primary" disabled={loading} style={{ width: '100%', padding: '14px', background: '#294b5a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                                            {loading ? 'Verifying...' : 'Verify Code'}
                                        </button>
                                    </form>
                                )}

                                {/* STEP 3: New Password Form */}
                                {step === 3 && (
                                    <form onSubmit={handleResetPassword}>
                                        <div className="corp-input-group" style={{ marginBottom: '15px', position: 'relative' }}>
                                            <label className="corp-input-label">New password</label>
                                            <div style={{ position: 'relative' }}>
                                                <input 
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    className="corp-input"
                                                    placeholder="••••••••"
                                                    style={{ width: '100%', padding: '12px', paddingRight: '45px', border: '2px solid #cbd5e1', borderRadius: '8px' }}
                                                    required 
                                                />
                                                <button 
                                                    type="button" 
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>
                                                    {showPassword ? 'Hide' : 'Show'}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="corp-input-group" style={{ marginBottom: '20px', position: 'relative' }}>
                                            <label className="corp-input-label">Confirm password</label>
                                            <div style={{ position: 'relative' }}>
                                                <input 
                                                    type={showPasswordConfirmation ? 'text' : 'password'}
                                                    value={passwordConfirmation}
                                                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                                                    className="corp-input"
                                                    placeholder="••••••••"
                                                    style={{ width: '100%', padding: '12px', paddingRight: '45px', border: '2px solid #cbd5e1', borderRadius: '8px' }}
                                                    required 
                                                />
                                                <button 
                                                    type="button" 
                                                    onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                                                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>
                                                    {showPasswordConfirmation ? 'Hide' : 'Show'}
                                                </button>
                                            </div>
                                        </div>
                                        <button type="submit" className="corp-btn-primary" disabled={loading} style={{ width: '100%', padding: '14px', background: '#294b5a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                                            {loading ? 'Updating...' : 'Reset Password'}
                                        </button>
                                    </form>
                                )}

                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <a href="/login" style={{ color: '#294b5a', textDecoration: 'none', fontSize: '14px' }}>← Back to login</a>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <DefaultFooter />
            <WhatsAppButton />
        </div>
    );
}

export default ForgotPassword;